import React, { useState } from 'react';
import { Link, withRouter } from 'react-router-dom';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import ReactApexChart from 'react-apexcharts';
import { Spin } from 'antd';
import H3Text from '@Uilib/h3Text';
import './style.scss';
import Helpers from '@Apis/helpers';
import dayjs from 'dayjs';

function MetricStackedBarChart({
  data, loading, title, selectedTenant, fromDate, toDate,
}) {
  const [selectedDepartment, setSelectedDepartment] = useState(null);

  // Extract unique departments
  const departments = Array.from(new Set(data.map((item) => item.department)));

  // Extract unique status
  const statuses = Array.from(new Set(data.map((item) => item.status)));

  // Prepare series data for ApexCharts
  const seriesData = statuses.map((status) => {
    const statusData = data.filter((item) => item.status === status);
    return {
      name: status,
      data: departments.map((department) => {
        const item = statusData.find((item) => item.department === department);
        return item?.value || 0;
      }),
    };
  });

  const handleSegmentClick = (event, chartContext, config) => {
    const department = departments[config.dataPointIndex];
    const status = statuses[config.seriesIndex].replaceAll(' ', '_').toUpperCase();
    const departmentId = data?.find((item) => item.department === department)?.department_id;

    const url = Helpers.appendSystemDefaultFilterIdInURL(`/purchase/purchase-request?page=1&limit=10&tenant=${selectedTenant}&status=${status}&department_ids=${departmentId}&startDate=${fromDate}&endDate=${toDate}`);
    window.open(url, '_blank');
  };

  const chartOptions = {
    chart: {
      type: 'bar',
      height: 250,
      stacked: true,
      events: {
        dataPointSelection: handleSegmentClick,
      },
      toolbar: {
        show: false,
      },
    },
    plotOptions: {
      bar: {
        horizontal: false,
        columnWidth: '45%',
      },
    },
    dataLabels: {
      enabled: false,
    },
    xaxis: {
      categories: departments,
      labels: {
        show: true,
      },
    },
    colors: statuses.map((status) => data.find((item) => item.status === status)?.color),
    legend: {
      position: 'bottom',
      horizontalAlign: 'center',
      offsetY: 10,
    },
    tooltip: {
      custom: ({
        series, seriesIndex, dataPointIndex,
      }) => {
        const department = departments[dataPointIndex];
        const status = statuses[seriesIndex];
        const item = data.find((items) => items.department === department && items.status === status);
        if (!item) return '';
        return (
          `<div class="tooltip-value" style="padding: 8px;">
            <div class="tooltip-value__content">
              <div class="tooltip-value__row" style="display: flex; gap: 8px; align-items: center; padding: 4px;">
                <div style="background-color: ${item.color}; width: 10px; height: 10px;" class="tooltip-value__bullet"></div>
                <h3 class="tooltip-value__name" style="margin: 0;">${item.status}</h3>
                <h3 class="tooltip-value__count" style="margin: 0;">${item.value}</h3>
              </div>
            </div>
          </div>`
        );
      },
    },
  };

  return (
    <div className="metric-stacked-bar__loader">
      {loading && (
        <div className="widget-loader">
          <Spin />
        </div>
      )}
      <div className="metric-stacked-bar">
        <H3Text text={title} className="metric-stacked-bar__title" />
        <div className="metric-stacked-bar__content">
          <div className="metric-stacked-bar__chart">
            <ReactApexChart options={chartOptions} series={seriesData} type="bar" height={250} />
          </div>
          <div className="metric-pie-chart__legend">
            {statuses.map((status) => (
              <div
                className={`legend__item ${selectedDepartment === status ? 'legend__item-selected' : ''}`}
                key={status}
                onClick={() => setSelectedDepartment(status)}
              >
                <div className="legend__item-bullet" style={{ backgroundColor: Helpers.getStatusColor(status?.replaceAll(' ', '_')?.toUpperCase())?.color }} />
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

const mapStateToProps = ({ UserReducers }) => ({
  user: UserReducers.user,
});

const mapDispatchToProps = (dispatch) => ({});

MetricStackedBarChart.propTypes = {
  data: PropTypes.array,
  loading: PropTypes.bool,
  title: PropTypes.string,
  selectedTenant: PropTypes.string,
  fromDate: PropTypes.any,
  toDate: PropTypes.any,
};

export default connect(mapStateToProps, mapDispatchToProps)(withRouter(MetricStackedBarChart));
